import {
  Broadcast,
  hostPptAnimationsLauncher,
  modifyLogo,
  opsPptAnimationsLauncher,
  updateSlideAsset,
  modifyOverlay
} from '@goldcast/api/backend';

import { AnimatedDeck, AnimatedSlide, Asset, Deck, SidebarState, Slide } from '.';
import { drawAnimatedSlideFrame, isCanvasCapturable, waitForAnimatedSlidesPlayer } from './animatedSlidesCanvas';
import {
  ANIMATED_SLIDES_CANVAS_FPS,
  ANIMATED_SLIDES_PLAYER_READY_TIMEOUT_MS,
  LOCAL_RECORDING_SLIDES_CANVAS_FPS,
  LOCAL_RECORDING_SLIDES_PUBLISHING_TIMEOUT
} from './constants';
import { SidbarStateGetters } from './getters';
import { SidebarStateMutations } from './mutations';
import {
  getSlidesCanvas,
  stopSharingSlidesCanvas,
  unshareSlidesOnLeave,
  updateSlidesCanvas,
  executeSlideNavigation,
  getAgoraCustomTrack
} from './utils';

import type { User } from '@/store/modules/event/types';

import analytics from '@/analytics';
import { AGORA, getEnv } from '@/constants';
import { showUserModalUsingUuid, updateBroadcast } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { ActivityType, handleSpeakerActivity } from '@/services/speakerActivity.service';
import store, { RootState } from '@/store';
import { getUsersByMmidAndAddtoStore } from '@/store/schedulers';

export type SidebarContext = {
  state: SidebarState;
  rootState: RootState;
  getters: SidbarStateGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof SidebarStateMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
};

// getSlidesCanvas reuses the existing canvas object, so an earlier start's handler is
// still on window holding that start's broadcastId and token.
function registerSlidesUnloadHandler(canvasState: { unloadHandler?: () => void }, unloadHandler: () => void): void {
  if (canvasState.unloadHandler) {
    window.removeEventListener('unload', canvasState.unloadHandler);
  }
  window.addEventListener('unload', unloadHandler);
  canvasState.unloadHandler = unloadHandler;
}

export default {
  setStageChatCurrentTabIndex(context: SidebarContext, index: number) {
    context.commit('changePrimaryTab', 'stage_chat');
    context.commit('setStageChatCurrentTabIndex', index);
  },
  openDriftTabIfDefault(context: SidebarContext) {
    if (context.rootState.event.event.drift_landing_as_default) {
      const tabindex = (context.getters.stageChatTabs as any).findIndex(({ id }: any) => id === 'drift_embed');
      context.commit<any>('setStageChatCurrentTabIndex', tabindex);
    }
  },
  // This action is there becaue the unreadall endpoint doesnt work for
  // fresh users. So the only way to safely maintain the accumulation of
  // all possible unreads is to fetch all the dms whenever a dm is actually
  // received through a socket event.
  // This way I do not have to try to keep track of which accumulation of
  // unreads I fetched from the api vs which I fetched from the socket and
  // whether or not the local state is out of sync from the actual backend
  // state bacause of that.
  triggerFetchOfAllDms(context: SidebarContext) {
    if (context.rootState.event.event?.p2p_chat_disabled) {
      return;
    }
    let needToFetchUsers = Promise.resolve([] as User[]);
    let directchats: string[] = context.rootState?.event?.avroom?.dmchannels ?? [];
    const currentUserMmid = context.rootState.user.currentUser?.mmid ?? '';
    const usersToGet = [];
    for (const chatid of directchats) {
      const channelName = context.rootState.mattermost.joinedChannels.value[chatid]?.name ?? '';
      const mmids = channelName.split('__').filter(it => it !== currentUserMmid);
      const otherUserMmid = mmids[0];
      if (otherUserMmid && !(context.rootState.event?.event?.users ?? {})[otherUserMmid]) {
        //@ts-ignore
        usersToGet.push(otherUserMmid);
      }
    }

    if (usersToGet.length > 0) {
      const eventid = context.rootState.event.event?.id ?? '';
      if (!eventid) {
        loggerService.warn('Eventid not found!');
      }
      needToFetchUsers = getUsersByMmidAndAddtoStore(eventid, usersToGet);
    }

    needToFetchUsers.then(() => {
      directchats = (directchats || []).filter(chatid => {
        const chatChannelName = context.rootState.mattermost.joinedChannels.value[chatid]?.name;
        if (!chatChannelName) {
          return;
        }
        if (context.rootGetters['mattermost/otherUser'](chatChannelName.split('__'))) {
          return true;
        }
        loggerService.info('Not going to fetch for this chat as user is not in the store: ', chatid);
        return false;
      });

      if (
        (!directchats || context.rootState.mattermost.joinedChannels.state !== 'loaded') &&
        context.state.timesWaitedForDMs < 5
      ) {
        context.state.timesWaitedForDMs += 1;
        loggerService.warn('Not found any dm channels. Waiting for them to download!');
        // In case directchat channel ids have not been downloaded, wait 2 seconds
        setTimeout(() => context.dispatch('triggerFetchOfAllDms'), 2000);
      } else if (!directchats) {
        context.state.timesWaitedForDMs = 0;
        loggerService.warn('Waited for dms to download for 5 times. Try to fetch dms again!');
        context
          .dispatch('event/avroom/getCurrentUserDMs', null, { root: true })
          .then(() => {
            context.dispatch('triggerFetchOfAllDms');
          })
          .catch(err => {
            loggerService.error('Failed to fetch dms! bailing!', err);
          });
      } else {
        context.dispatch('initializeDMs', directchats);
      }
    });
  },
  initializeDMs(context: SidebarContext, directchats: string[]) {
    if (!context.state.dmsInitialized) {
      context.state.dmsInitialized = true;
      for (const channel_id of directchats) {
        context.dispatch(
          'event/avroom/fetchInitialMessages',
          { channel_id, fetchPinnedMessages: false },
          {
            root: true
          }
        );
        if (!context.rootGetters['event/isMmApiConsolidationActive']) {
          // fetchMessages already calls fetchNumUnread; skip when MM_API_CONSOLIDATION is on
          context.dispatch(
            'event/avroom/fetchNumUnread',
            {
              channel_id: channel_id,
              user_id: context.rootState.user.currentUser?.mmid
            },
            { root: true }
          );
        }
      }
    }
  },
  showSpeakerChatDescriptionBubble(context: SidebarContext) {
    context.state.showSpeakerChatDescription = true;
    setTimeout(() => {
      context.state.showSpeakerChatDescription = false;
    }, 5000);
  },
  loadDecks(context: SidebarContext, assets: Asset[]) {
    const useNewSlideAnimation = context.rootState.event.useNewSlideAnimation;
    assets.forEach(asset => {
      if (useNewSlideAnimation && asset.is_animations_enabled) {
        return;
      }
      context.dispatch('loadDeck', { asset });
    });
  },
  async loadDeck(context: SidebarContext, { asset, retries = 3 }: { asset: Asset; retries?: number }) {
    try {
      const deckInStore = context.state.slideDecks.slides[asset.id];
      if (deckInStore?.transcodeStatus === asset.transcode_status) {
        return;
      }
      if (!asset.urls.config) {
        return;
      }
      const slides: Slide[] = await (await fetch(asset.urls.config, { cache: 'force-cache' })).json();
      const deck: Deck = {
        id: asset.id,
        name: asset.name,
        pdfUrl: asset.urls.pdf,
        configUrl: asset.urls.config,
        thumbnail: slides[0].thumb,
        numPages: slides.length,
        slides: slides.map(s => ({
          thumb: `${asset.urls.pdf}/${s.thumb}`,
          low: `${asset.urls.pdf}/${s.low}`,
          high: `${asset.urls.pdf}/${s.high}`,
          speaker_notes: s.speaker_notes
        })),
        hasSpeakerNotes: slides.some(s => s.speaker_notes),
        transcodeStatus: asset.transcode_status,
        embeddedUrl: asset.embedded_url
      };
      context.commit('addDeck', deck);
    } catch (e) {
      if (retries > 0) {
        loggerService.warn('Error loading deck!', e);
        setTimeout(() => {
          context.dispatch('loadDeck', { asset, retries: retries - 1 });
        }, 1000);
        return;
      }

      loggerService.error('Error loading deck, unable to load after 3 retries ', e);
    }
  },
  loadBroadcastSlides(context: SidebarContext, slides: Asset[]) {
    const useNewSlideAnimation = context.rootState.event.useNewSlideAnimation;
    const currentSlides = context.state.slideDecks.slides;
    Object.keys(currentSlides).forEach(slideId => {
      if (!slides.find(s => s.id === slideId)) {
        context.commit('removeSlideDeck', slideId);
      }
    });

    const regularSlides = useNewSlideAnimation ? slides.filter(s => !s.is_animations_enabled) : slides;

    // Auto-open only the broadcast's sole deck, and only when it first becomes COMPLETE,
    // so later slide_assets updates don't re-expand a deck the user collapsed.
    if (slides.length === 1 && regularSlides.length === 1) {
      const [deck] = regularSlides;
      if (deck.transcode_status !== 'COMPLETE') {
        context.commit('setCurrentDeck', undefined);
      } else if (currentSlides[deck.id]?.transcodeStatus !== 'COMPLETE') {
        context.commit('setCurrentDeck', deck.id);
      }
    }
    context.dispatch('loadDecks', slides);
  },

  loadAnimatedDecks(context: SidebarContext, assets: Asset[]) {
    assets.forEach(asset => {
      if (asset.is_animations_enabled) {
        context.dispatch('loadAnimatedDeck', { asset });
      }
    });
  },

  async fetchSpeakerNotes(
    context: SidebarContext,
    { presUrl, assetId }: { presUrl: string; assetId: string }
  ): Promise<Record<number, string>> {
    const speakerNotes: Record<number, string> = {};
    try {
      const notesUrl = `${presUrl}/pres/notes.xml`;
      let notesResponse = await fetch(notesUrl, { method: 'GET', cache: 'force-cache' });
      if (!notesResponse.ok) {
        notesResponse = await fetch(notesUrl, { method: 'GET', cache: 'no-store' });
      }
      if (notesResponse.ok) {
        const notesXml = await notesResponse.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(notesXml, 'text/xml');
        const slideElements = xmlDoc.querySelectorAll('sld');
        slideElements.forEach(slideEl => {
          const slideNo = parseInt(slideEl.getAttribute('no') || '0', 10);
          if (slideNo > 0) {
            const words = slideEl.querySelectorAll('wrd');
            const noteText = Array.from(words)
              .map(wrd => wrd.getAttribute('txt') || '')
              .join(' ')
              .trim();
            if (noteText) {
              speakerNotes[slideNo] = noteText;
            }
          }
        });
      }
    } catch (notesError) {
      loggerService.error('Error loading speaker notes for animated deck', {
        assetId,
        presUrl,
        error: notesError
      });
    }

    return speakerNotes;
  },

  async fetchAnimatedSlideThumbnails(
    context: SidebarContext,
    {
      presUrl,
      speakerNotes,
      assetId,
      transcodeStatus,
      slideConversionStatus
    }: {
      presUrl: string;
      speakerNotes: Record<number, string>;
      assetId: string;
      transcodeStatus: Asset['transcode_status'];
      slideConversionStatus: Asset['slide_conversion_status'];
    }
  ): Promise<AnimatedSlide[]> {
    const slides: AnimatedSlide[] = [];
    let slideNumber = 1;
    const maxSlides = 1000;
    const fetchThumb = async (url: string): Promise<Response> => {
      const response = await fetch(url, { method: 'GET', cache: 'force-cache' });
      if (!response.ok) {
        return fetch(url, { method: 'GET', cache: 'no-store' });
      }
      return response;
    };

    while (slideNumber <= maxSlides) {
      const thumbUrl = `${presUrl}/Thumbnails/Slide${slideNumber}.PNG`;
      try {
        const response = await fetchThumb(thumbUrl);
        if (!response.ok) {
          if (
            response.status === 403 &&
            slides.length === 0 &&
            transcodeStatus === 'COMPLETE' &&
            slideConversionStatus === 'COMPLETE'
          ) {
            loggerService.error(
              'Slide thumbnail returned 403 despite transcode and slide conversion both being complete',
              { assetId, thumbUrl, transcodeStatus, slideConversionStatus }
            );
          }
          break;
        }
        slides.push({
          slideNumber,
          thumb: thumbUrl,
          high: thumbUrl,
          speakerNotes: speakerNotes[slideNumber] || ''
        });
        slideNumber++;
      } catch {
        break;
      }
    }

    return slides;
  },

  async loadAnimatedDeck(context: SidebarContext, { asset, retries = 3 }: { asset: Asset; retries?: number }) {
    try {
      const animatedDeckInStore = context.state.animatedSlideDecks?.decks?.[asset.id];
      if (animatedDeckInStore?.transcodeStatus === asset.transcode_status) {
        return;
      }

      if (asset.slide_conversion_status === 'ERROR') {
        if (asset.urls?.pdf && asset.urls?.config) {
          context.dispatch('loadDeck', { asset, retries });
        }
        return;
      }

      if (
        context.rootGetters['event/isRSAnimatedSlidesEnabled'] &&
        (asset.slide_conversion_status !== 'COMPLETE' || asset.transcode_status !== 'COMPLETE')
      ) {
        return;
      }

      if (asset.slide_conversion_status === 'PROGRESSING') {
        return;
      }

      const cdnBaseUrl = getEnv('VUE_APP_CLOUD_FRONT_URL_v2');
      const presUrl = `${cdnBaseUrl}${asset.path}`;

      const isOrganizer = context.rootGetters['event/isOrganizerOrSpeaker'];

      let speakerNotes: Record<number, string> = {};
      let slides: AnimatedSlide[] = [];

      if (isOrganizer || context.rootGetters['event/isRSAnimatedSlidesEnabled']) {
        speakerNotes = await context.dispatch('fetchSpeakerNotes', { presUrl, assetId: asset.id });
        slides = await context.dispatch('fetchAnimatedSlideThumbnails', {
          presUrl,
          speakerNotes,
          assetId: asset.id,
          transcodeStatus: asset.transcode_status,
          slideConversionStatus: asset.slide_conversion_status
        });
        if (slides.length === 0) {
          loggerService.error('[animated-slides] No slides found for animated deck', { assetId: asset.id, presUrl });
          if (asset.urls?.pdf && asset.urls?.config) {
            context.dispatch('loadDeck', { asset, retries });
          }
          return;
        }
      } else {
        // these users don't require notes and thumbnails
        slides = [
          {
            slideNumber: 1,
            thumb: '',
            high: '',
            speakerNotes: ''
          }
        ];
      }

      const numSlides = slides.length;

      const deck: AnimatedDeck = {
        id: asset.id,
        name: asset.name,
        path: asset.path,
        cdnBaseUrl,
        thumbnail: slides[0]?.thumb || '',
        numSlides,
        slides,
        transcodeStatus: asset.transcode_status,
        hasSpeakerNotes: slides.some(s => !!s.speakerNotes),
        is_animations_enabled: true
      };

      context.commit('addAnimatedDeck', deck);

      // Also store as regular deck for fallback when animation conversion fails
      const regularDeck: Deck = {
        id: asset.id,
        name: asset.name,
        pdfUrl: presUrl,
        configUrl: presUrl,
        thumbnail: slides[0]?.thumb || '',
        numPages: numSlides,
        slides: slides.map(s => ({
          thumb: s.thumb,
          low: s.high,
          high: s.high,
          speaker_notes: s.speakerNotes
        })),
        hasSpeakerNotes: slides.some(s => !!s.speakerNotes),
        transcodeStatus: asset.transcode_status,
        embeddedUrl: asset.embedded_url || ''
      };
      context.commit('addDeck', regularDeck);
    } catch (e) {
      if (retries > 0) {
        loggerService.warn('[animated-slides] Error loading animated deck, retrying...', e);
        setTimeout(() => {
          context.dispatch('loadAnimatedDeck', { asset, retries: retries - 1 });
        }, 1000);
        return;
      }

      loggerService.error('[animated-slides] Error loading animated deck after 3 retries', e);
      if (asset.urls?.pdf && asset.urls?.config) {
        context.dispatch('loadDeck', { asset, retries: 3 });
      }
    }
  },

  loadBroadcastAnimatedSlides(context: SidebarContext, slides: Asset[]) {
    if (!context.rootState.event.useNewSlideAnimation) {
      return;
    }

    const animatedSlides = slides.filter(s => s.is_animations_enabled);

    const currentAnimatedDecks = context.state.animatedSlideDecks?.decks;
    if (currentAnimatedDecks) {
      Object.keys(currentAnimatedDecks).forEach(deckId => {
        if (!animatedSlides.find(s => s.id === deckId)) {
          context.commit('removeAnimatedDeck', deckId);
        }
      });
    }

    // Same auto-open rule as loadBroadcastSlides.
    if (slides.length === 1 && animatedSlides.length === 1) {
      const [deck] = animatedSlides;
      if (deck.transcode_status !== 'COMPLETE') {
        context.commit('setCurrentAnimatedDeck', '');
      } else if (currentAnimatedDecks?.[deck.id]?.transcodeStatus !== 'COMPLETE') {
        context.commit('setCurrentAnimatedDeck', deck.id);
      }
    }

    context.dispatch('loadAnimatedDecks', animatedSlides);
  },

  hostSlideAnimations(context: SidebarContext) {
    hostPptAnimationsLauncher({
      body: {
        broadcasts: [context.rootGetters['event/currentBroadcast']?.id]
      }
    });
  },
  playDeckWithAnimations(context: SidebarContext, deckId: string) {
    return opsPptAnimationsLauncher({
      body: {
        broadcast: context.rootGetters['event/currentBroadcast']?.id,
        operation: 'startScreenShare',
        slide_asset: deckId
      }
    });
  },
  startOverDeckWithAnimations(context: SidebarContext, deckId: string) {
    return opsPptAnimationsLauncher({
      body: {
        broadcast: context.rootGetters['event/currentBroadcast']?.id,
        operation: 'startOver',
        slide_asset: deckId
      }
    });
  },
  playNextSlide(context: SidebarContext) {
    const publishedStage = context.getters.publishedStage as any;
    if (publishedStage.currentScreen !== 'slide_animations') {
      return;
    }
    const deckId = (context.getters.publishedStage as any)?.params.id;
    return opsPptAnimationsLauncher({
      body: {
        broadcast: context.rootGetters['event/currentBroadcast']?.id,
        operation: 'moveRight',
        slide_asset: deckId
      }
    });
  },
  playPreviousSlide(context: SidebarContext) {
    const publishedStage = context.getters.publishedStage as any;
    if (publishedStage.currentScreen !== 'slide_animations') {
      return;
    }
    const deckId = (context.getters.publishedStage as any)?.params.id;
    return opsPptAnimationsLauncher({
      body: {
        broadcast: context.rootGetters['event/currentBroadcast']?.id,
        operation: 'moveLeft',
        slide_asset: deckId
      }
    });
  },
  stopPlayingSlides(context: SidebarContext) {
    const publishedStage = context.getters.publishedStage as any;
    if (publishedStage.currentScreen !== 'slide_animations') {
      return;
    }
    const deckId = (context.getters.publishedStage as any)?.params.id;
    return opsPptAnimationsLauncher({
      body: {
        broadcast: context.rootGetters['event/currentBroadcast']?.id,
        operation: 'stopScreenShare',
        slide_asset: deckId
      }
    });
  },
  setSlidePage(context: SidebarContext, { slide, page }: { slide: string; page: number }): Promise<any> | undefined {
    const { isOverridable, currentScreen } = context.getters.publishedStage as any;
    if (!isOverridable) {
      return;
    }

    if (currentScreen !== 'slide') {
      context.dispatch('unpublish');
    }

    if (slide) {
      analytics.track('slide_share_start');
    } else {
      analytics.track('slide_share_stop');
    }

    if (context.state.rsSlidesCanvas) {
      const url = context.state.slideDecks?.slides[slide]?.slides[page]?.high;
      context.dispatch('updateSlidesCanvas', url);
    }

    return context.dispatch('manageSlides', { slide_asset: slide, slide_number: page });
  },
  unpublish(context: SidebarContext) {
    const { currentScreen, title } = context.getters.publishedStage as any;
    if (currentScreen === 'screen') {
      handleSpeakerActivity(
        () =>
          context.dispatch(
            'mediaroom/stopScreenSharing',
            {
              channelName: context.rootState.route.params.id
            },
            { root: true }
          ),
        ActivityType.ScreenShareStop,
        ActivityType.ScreenShareStopFailed
      );
    } else if (currentScreen === 'slide_animations') {
      context.dispatch('stopPlayingSlides');
    } else if (currentScreen === 'new_slide_animations') {
      // In the Recording Studio the deck is on stage as a canvas-backed screen-share track, so
      // unpublishing has to release that track too. The compositor's own frame loop would also
      // notice the screen change, but tearing down here keeps it deterministic - same as 'slide'.
      if (context.rootGetters['event/isRSAnimatedSlidesEnabled'] && context.state.rsSlidesCanvas) {
        loggerService.info('[Local Recording] Animated slides: Unpublishing deck, stopping canvas');
        context.dispatch(
          'mediaroom/stopScreenSharing',
          {
            channelName: context.rootState.route.params.id
          },
          { root: true }
        );
        context.dispatch('stopSlidesCanvas');
      }
      handleSpeakerActivity(
        () => context.dispatch('stopNewAnimatedSlides'),
        ActivityType.SlideShareStop,
        ActivityType.SlideShareStopFailed,
        {
          title
        }
      );
    } else if (currentScreen === 'textQna') {
      context.dispatch('event/textQNA/unPublishCurrentTextQNA', null, {
        root: true
      });
    } else if (currentScreen === 'poll') {
      handleSpeakerActivity(
        () => context.dispatch('event/avroom/unPublishPoll', null, { root: true }),
        ActivityType.PollUnShare,
        ActivityType.PollUnShareFailed
      );
    } else if (currentScreen === 'slide') {
      loggerService.info('[Local Recording] Slides: Unpublishing slides, stopping canvas');
      const isLocalSlidesRecordingEnabled = context.rootGetters['event/isLocalSlidesRecordingEnabled'];
      const areLocalSlidesPublished =
        context.getters['areLocalSlidesPublished'] ||
        (context.getters['slideTabProps'] as any)?.currentDeck?.isPublished;
      if (isLocalSlidesRecordingEnabled && !!areLocalSlidesPublished) {
        context.dispatch(
          'mediaroom/stopScreenSharing',
          {
            channelName: context.rootState.route.params.id
          },
          { root: true }
        );
        context.dispatch('stopSlidesCanvas');
      }
      handleSpeakerActivity(
        () => context.dispatch('setSlidePage', { slide: null, page: 0 }),
        ActivityType.SlideShareStop,
        ActivityType.SlideShareStopFailed,
        {
          title
        }
      );
    } else if (currentScreen === 'broadcastOverlay') {
      handleSpeakerActivity(
        () => context.dispatch('manageOverlay', { overlay_text: null, show_overlay: false }),
        ActivityType.OverlayStop,
        ActivityType.OverlayStopFailed
      );
    } else if (currentScreen === 'live_hls') {
      if (context.rootState.liveVideoRoom.isSwitchingToSpeakerMode) {
        return;
      }
      context.dispatch('liveVideoRoom/switchToSpeakerMode', null, { root: true });
    }
  },
  openP2pChat(context: SidebarContext, user: User) {
    context.commit('changePrimaryTab', 'messages_chat');
    return context.dispatch('event/avroom/openP2PChat', user.mmid, {
      root: true
    });
  },
  showSpeaker(context: SidebarContext, speaker: User) {
    showUserModalUsingUuid(speaker?.id);
    analytics.track('view_speaker_profile', {
      eventId: context.rootState.route?.params?.eventID,
      broadcastId: context.rootState.route?.params?.id,
      isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker']
    });
  },
  async stopSharingBroadcastTicker(context: SidebarContext) {
    await updateBroadcast(context.rootGetters['event/currentBroadcast']?.id, {
      ticker_text: null,
      ticker_url: null,
      show_ticker: false
    });
  },
  async updateSlides(context: SidebarContext, patch: Partial<Broadcast>) {
    await updateBroadcast(context.rootGetters['event/currentBroadcast']?.id, patch);
  },
  async manageLogo(context: SidebarContext, patch: Partial<Broadcast>) {
    const isAStaffMember = context.rootGetters['isStaffMember'];
    const broadcastId = context.rootGetters['event/currentBroadcast'].id;
    if (isAStaffMember) {
      await modifyLogo({ body: { stage_logo_spec: patch.stage_logo_spec }, id: broadcastId });
    } else {
      await updateBroadcast(broadcastId, {
        stage_logo_spec: patch.stage_logo_spec
      });
    }
  },

  async manageOverlay(context: SidebarContext, { overlay_text, show_overlay }) {
    const broadcastId = context.rootGetters['event/currentBroadcast']?.id;
    const isAStaffMember = context.rootGetters['isStaffMember'];
    if (isAStaffMember) {
      return modifyOverlay({
        id: broadcastId,
        body: {
          overlay_text,
          show_overlay
        }
      });
    }
    return updateBroadcast(broadcastId, {
      overlay_text,
      show_overlay
    });
  },
  async manageSlides(
    context: SidebarContext,
    { slide_asset, slide_number }: { slide_asset: string; slide_number: number }
  ): Promise<any> {
    try {
      return updateSlideAsset({
        id: context.rootGetters['event/currentBroadcast']?.id,
        body: {
          current_playing_slide_number: slide_number,
          current_playing_slide_asset: slide_asset
        }
      });
    } catch (error) {
      loggerService.error('Error managing slides', error);
    }
  },
  /**
   * Initializes and starts the slides canvas for screen sharing
   *
   * This action:
   * 1. Creates a canvas element and cache if they don't exist
   * 2. Sets up the canvas dimensions (1920x1080)
   * 3. Starts an interval to continuously update the canvas with current slide
   * 4. Creates a video stream from the canvas
   * 5. Creates and returns an Agora video track from the stream
   *
   * The canvas is used to share slides as a video stream through Agora RTC.
   * It updates at the rate specified by LOCAL_RECORDING_SLIDES_CANVAS_FPS.
   *
   * @param context - The Vuex action context containing state and dispatch functions
   * @returns Promise<AgoraRTC.ILocalVideoTrack[]> - Array containing the created video track, or empty if creation fails
   */
  async startSlidesCanvas(context: SidebarContext) {
    context.dispatch('setIsPublishing', true);
    analytics.track('start_local_slides_share_canvas');
    context.state.rsSlidesCanvas = getSlidesCanvas(context.state.rsSlidesCanvas);

    // Load the initial image
    context.dispatch('updateSlidesCanvas', context.getters.currentSlideUrl);

    // Store the unload handler function reference for proper cleanup
    if (!context.rootGetters['event/isLocalSlidesRecordingEnabled']) {
      const broadcastId = context.rootGetters['event/currentBroadcast']?.id;
      const userToken = store.state.user?.accessToken;
      const unloadHandler = () => {
        unshareSlidesOnLeave(broadcastId, userToken);
      };
      registerSlidesUnloadHandler(context.state.rsSlidesCanvas, unloadHandler);
    }
    let intervalCount = 0;
    context.state.rsSlidesCanvas.slidesCanvasInterval = setInterval(() => {
      if (!(context.getters.publishedStage as any).currentScreen) {
        // In case other users stop sharing slides, we stop this user's local slides
        context.dispatch(
          'mediaroom/stopScreenSharing',
          {
            channelName: context.rootState.route.params.id
          },
          { root: true }
        );
        context.dispatch('stopLocalSlideRecording');
        return;
      }
      context.dispatch('updateSlidesCanvas', context.getters.currentSlideUrl);
      if (context.state.isPublishingRSSlides) {
        context.dispatch('checkIfRSSlidesArePublished', intervalCount++);
      }
    }, 1000 / LOCAL_RECORDING_SLIDES_CANVAS_FPS);

    // Get the canvas stream
    const stream = context.state.rsSlidesCanvas?.canvasElement.captureStream(LOCAL_RECORDING_SLIDES_CANVAS_FPS);

    const track = stream.getVideoTracks()[0];

    try {
      const imageTrack = await getAgoraCustomTrack(context.rootState.event, track);
      imageTrack.on('track-ended', () => {
        loggerService.info('[Local Recording] Slides: Track ended, stopping canvas');
        context.dispatch(
          'mediaroom/stopScreenSharing',
          {
            channelName: context.rootState.route.params.id
          },
          { root: true }
        );
        store.dispatch('setSlidePage', { slide: null, page: 0 });
      });

      return [imageTrack];
    } catch (error) {
      loggerService.error('[Local Recording] Slides: Error creating canvas image track', error);
      return [];
    }
  },
  /**
   * Recorder-side counterpart of `startSlidesCanvas` for animated decks.
   *
   * The animated player renders into a same-origin iframe rather than into an image, so there is
   * no single element to capture. This action waits for that player to render, then composites
   * its subtree onto an offscreen canvas at ANIMATED_SLIDES_CANVAS_FPS and publishes that canvas
   * as an Agora custom video track.
   *
   * @param context - The Vuex action context containing state and dispatch functions
   * @returns Promise<AgoraRTC.ILocalVideoTrack[]> - Array containing the created video track, or empty if creation fails
   */
  async startAnimatedSlidesCanvas(context: SidebarContext) {
    context.dispatch('setIsPublishing', true);
    analytics.track('start_local_animated_slides_share_canvas');

    // Claimed before the first await: handleRecorderSlidesCanvas runs on every heartbeat and uses
    // rsSlidesCanvas to tell "already recording" from "start now", so leaving it unset while we
    // wait for the player would start a second recording on the next tick.
    context.state.rsSlidesCanvas = getSlidesCanvas(context.state.rsSlidesCanvas);
    const canvasState = context.state.rsSlidesCanvas;

    if (!context.rootGetters['event/isLocalSlidesRecordingEnabled']) {
      const broadcastId = context.rootGetters['event/currentBroadcast']?.id;
      const userToken = store.state.user?.accessToken;
      const unloadHandler = () => {
        unshareSlidesOnLeave(broadcastId, userToken);
      };
      registerSlidesUnloadHandler(canvasState, unloadHandler);
    }

    let root: HTMLElement;
    try {
      root = await waitForAnimatedSlidesPlayer(ANIMATED_SLIDES_PLAYER_READY_TIMEOUT_MS);
    } catch (error) {
      loggerService.error('[Local Recording] Animated slides: player never rendered', error);
      // Released so a later heartbeat can retry instead of us holding the sentinel forever.
      context.dispatch('stopSlidesCanvas');
      context.dispatch('setIsPublishing', false);
      return [];
    }

    const iframeDoc = root.ownerDocument;
    if (iframeDoc) {
      for (const fontFace of iframeDoc.fonts) {
        document.fonts.add(fontFace);
      }
      await document.fonts.ready;
    }

    drawAnimatedSlideFrame(canvasState.canvasElement);
    if (!isCanvasCapturable(canvasState.canvasElement)) {
      context.dispatch('stopSlidesCanvas');
      context.dispatch('setIsPublishing', false);
      return [];
    }

    const frameInterval = 1000 / ANIMATED_SLIDES_CANVAS_FPS;
    let lastFrameAt = 0;
    // checkIfRSSlidesArePublished budgets LOCAL_RECORDING_SLIDES_PUBLISHING_TIMEOUT ticks, which
    // the static canvas spends one per second. Keep that pacing rather than burning the budget in
    // a fraction of a second at the animation frame rate.
    let lastPublishCheckAt = 0;
    let publishCheckCount = 0;

    const stopAnimatedSlidesShare = () => {
      context.dispatch(
        'mediaroom/stopScreenSharing',
        {
          channelName: context.rootState.route.params.id
        },
        { root: true }
      );
      context.dispatch('stopSlidesCanvas');
      context.dispatch('setIsPublishing', false);
    };

    // The deck can leave the stage without this user acting on it - another producer stops the share,
    // a poll gets published - so the share has to end on its own. Watched rather than polled from
    // renderFrame: `publishedStage` depends on `mediaroom.remoteStreams` and `screenTracks`, which
    // churn constantly during a call, so a per-frame read recomputed it (plus `slideTabProps` and
    // `animatedSlideTabProps`, both of which walk `broadcast.slide_assets`) up to
    // ANIMATED_SLIDES_CANVAS_FPS times a second. A watcher re-runs only when those dependencies
    // actually change, batched once per tick, and keeps the getter out of the frame loop entirely.
    canvasState.unwatchPublishedScreen = store.watch(
      (_state, getters) => getters['sidebar/publishedStage']?.currentScreen,
      currentScreen => {
        if (currentScreen === 'new_slide_animations') {
          return;
        }
        loggerService.info('[Local Recording] Animated slides: deck left the stage, stopping canvas', {
          currentScreen
        });
        stopAnimatedSlidesShare();
      }
    );

    const renderFrame = (timestamp: number) => {
      const currentCanvasState = context.state.rsSlidesCanvas;
      // Cleared by stopSlidesCanvas, which is how the watcher above ends this loop.
      if (!currentCanvasState) {
        return;
      }
      currentCanvasState.animationFrameId = requestAnimationFrame(renderFrame);

      if (timestamp - lastFrameAt < frameInterval) {
        return;
      }
      lastFrameAt = timestamp;

      drawAnimatedSlideFrame(currentCanvasState.canvasElement);
      if (context.state.isPublishingRSSlides && timestamp - lastPublishCheckAt >= 1000) {
        lastPublishCheckAt = timestamp;
        context.dispatch('checkIfRSSlidesArePublished', publishCheckCount++);
      }
    };
    canvasState.animationFrameId = requestAnimationFrame(renderFrame);

    try {
      const stream = canvasState.canvasElement.captureStream(ANIMATED_SLIDES_CANVAS_FPS);
      const animationTrack = await getAgoraCustomTrack(context.rootState.event, stream.getVideoTracks()[0]);
      animationTrack.on('track-ended', () => {
        loggerService.info('[Local Recording] Animated slides: Track ended, stopping canvas');
        stopAnimatedSlidesShare();
      });

      return [animationTrack];
    } catch (error) {
      loggerService.error('[Local Recording] Animated slides: Error creating canvas animation track', error);
      context.dispatch('stopSlidesCanvas');
      context.dispatch('setIsPublishing', false);
      return [];
    }
  },
  checkIfRSSlidesArePublished(context: SidebarContext, intervalCount: number) {
    if (intervalCount > LOCAL_RECORDING_SLIDES_PUBLISHING_TIMEOUT) {
      // handle timeout, after 5 interval cycles
      context.dispatch('setIsPublishing', false);
      return;
    }
    const remoteStreamsKeys = Object.keys(
      store.state.mediaroom.mediaRooms[context.rootState.route.params.id as string]?.remoteStreams || {}
    );
    const screenTrackKey = remoteStreamsKeys?.find((uid: string) => uid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM));
    if (screenTrackKey) {
      const visibleState =
        store.state.mediaroom.mediaRooms[context.rootState.route.params.id as string]?.remoteStreams[
          screenTrackKey
        ].videoTrack?.getVideoElementVisibleStatus();
      if (visibleState?.visible) {
        context.dispatch('setIsPublishing', false);
      }
    }
  },
  updateSlidesCanvas(context: SidebarContext, imageUrl: string) {
    if (!context.state.rsSlidesCanvas || !imageUrl) {
      return;
    }
    updateSlidesCanvas(context.state.rsSlidesCanvas.canvasElement, imageUrl);
  },
  stopSlidesCanvas(context: SidebarContext) {
    analytics.track('stop_local_slides_share', {
      hasCanvas: !!context.state.rsSlidesCanvas
    });
    if (context.state.rsSlidesCanvas) {
      stopSharingSlidesCanvas(context.state.rsSlidesCanvas);

      // Animated decks only - the static canvas never registers one.
      context.state.rsSlidesCanvas.unwatchPublishedScreen?.();

      // Remove the unload event listener using the stored function reference
      if (context.state.rsSlidesCanvas.unloadHandler) {
        window.removeEventListener('unload', context.state.rsSlidesCanvas.unloadHandler);
      }

      context.state.rsSlidesCanvas = undefined;
    }
  },
  stopLocalSlideRecording(context: SidebarContext) {
    if (context.state.rsSlidesCanvas) {
      context.dispatch('stopSlidesCanvas');
      if (!context.rootGetters['event/isLocalSlidesRecordingEnabled']) {
        context.dispatch('setSlidePage', { slide: null, page: 0 });
      }
    }
    context.dispatch('setIsPublishing', false);
  },
  setIsPublishing(context: SidebarContext, isPublishing: boolean) {
    context.state.isPublishingRSSlides = isPublishing;
  },

  async goToNextAnimatedStep(context: SidebarContext) {
    const isAnimatedSlidesEnabled = context.rootGetters['sidebar/isAnimatedSlidesEnabled'];
    if (!isAnimatedSlidesEnabled) {
      return;
    }

    const broadcast = context.rootGetters['event/currentBroadcast'];
    const currentSlide = broadcast.current_playing_slide_number ?? 1;
    const currentStep = broadcast.current_playing_step_number ?? 0;
    const maxStepCount = context.state.animatedSlides?.maxStepCount;

    let updatedSlide, updatedStep;

    if (maxStepCount === currentStep) {
      updatedSlide = currentSlide + 1;
      updatedStep = 0;
    } else {
      updatedSlide = currentSlide;
      updatedStep = currentStep + 1;
    }

    loggerService.log(
      '[animated-slides] mmaxStepCount, updateSlide, updatedStep',
      maxStepCount,
      updatedSlide,
      updatedStep
    );

    return executeSlideNavigation(
      broadcast,
      { action: 'next', updatedSlide, updatedStep, currentPlayingSlideAsset: broadcast.current_playing_slide_asset },
      data => context.dispatch('handleSlideVersionConflict', data, { root: false })
    );
  },

  async goToPrevAnimatedStep(context: SidebarContext) {
    const isAnimatedSlidesEnabled = context.rootGetters['sidebar/isAnimatedSlidesEnabled'];
    if (!isAnimatedSlidesEnabled) {
      return;
    }

    const broadcast = context.rootGetters['event/currentBroadcast'];
    const currentSlide = broadcast.current_playing_slide_number ?? 1;
    const currentStep = broadcast.current_playing_step_number ?? 0;

    let updatedSlide: number;
    let updatedStep: number;

    if (currentStep > 0) {
      updatedSlide = currentSlide;
      updatedStep = currentStep - 1;
    } else {
      updatedSlide = currentSlide - 1 > 0 ? currentSlide - 1 : 1;
      updatedStep = context?.state?.animatedSlides?.slideStepMap?.[updatedSlide] || 0;
    }

    loggerService.log('[animated-slides] updateSlide, updatedStep', updatedSlide, updatedStep);

    return executeSlideNavigation(
      broadcast,
      { action: 'prev', updatedSlide, updatedStep, currentPlayingSlideAsset: broadcast.current_playing_slide_asset },
      data => context.dispatch('handleSlideVersionConflict', data, { root: false })
    );
  },

  async goToNthAnimatedSlide(
    context: SidebarContext,
    { slideNumber, stepNumber = 0 }: { slideNumber: number; stepNumber?: number }
  ) {
    const isAnimatedSlidesEnabled = context.rootGetters['sidebar/isAnimatedSlidesEnabled'];
    if (!isAnimatedSlidesEnabled) {
      return;
    }

    const broadcast = context.rootGetters['event/currentBroadcast'];
    return executeSlideNavigation(
      broadcast,
      {
        action: 'go_to_nth',
        updatedSlide: slideNumber,
        updatedStep: stepNumber,
        currentPlayingSlideAsset: broadcast.current_playing_slide_asset
      },
      data => context.dispatch('handleSlideVersionConflict', data, { root: false })
    );
  },

  async startNewAnimatedSlides(
    context: SidebarContext,
    { deckId, slideNumber = 1 }: { deckId: string; slideNumber?: number }
  ) {
    const broadcast = context.rootGetters['event/currentBroadcast'];
    return executeSlideNavigation(
      broadcast,
      { action: 'start', updatedSlide: slideNumber, updatedStep: 0, currentPlayingSlideAsset: deckId },
      data => context.dispatch('handleSlideVersionConflict', data, { root: false })
    );
  },

  async stopNewAnimatedSlides(context: SidebarContext) {
    const broadcast = context.rootGetters['event/currentBroadcast'];
    if (!broadcast.current_playing_slide_asset) {
      return;
    }
    return executeSlideNavigation(
      broadcast,
      { action: 'stop', updatedSlide: null, updatedStep: null, currentPlayingSlideAsset: null },
      data => context.dispatch('handleSlideVersionConflict', data, { root: false })
    );
  },

  handleSlideVersionConflict(
    context: SidebarContext,
    conflictData: {
      slide_state_version?: { message: string; code: string };
      current_playing_slide_asset?: { message: string; code: string };
      current_playing_slide_number?: { message: string; code: string };
      current_playing_step_number?: { message: string; code: string };
      current_slide_state_version?: { message: string; code: string };
    }
  ) {
    loggerService.warn('Slide state version conflict detected, syncing with backend', conflictData);

    const assetId = conflictData.current_playing_slide_asset?.message || null;
    const slideNumber = parseInt(conflictData.current_playing_slide_number?.message || '0', 10);
    const stepNumber = parseInt(conflictData.current_playing_step_number?.message || '0', 10);
    const version = parseInt(conflictData.current_slide_state_version?.message || '1', 10);

    (context.commit as any)(
      'event/setNewSlideAnimationAssets',
      {
        id: assetId,
        current_playing_slide_number: slideNumber,
        current_playing_step_number: stepNumber,
        slide_state_version: version
      },
      { root: true }
    );

    context.commit('updateAnimatedSlidePosition', {
      slideIndex: slideNumber,
      stepNumber: stepNumber,
      slide_state_version: version
    });

    (context.commit as any)(
      'event/updateBroadcastSlideState',
      {
        broadcastId: context.rootGetters['event/currentBroadcast'].id,
        slide_state_version: version,
        current_playing_slide_asset: assetId,
        current_playing_slide_number: slideNumber,
        current_playing_step_number: stepNumber
      },
      { root: true }
    );
  }
};
